import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticSwitchgear } from "./realistic";

/**
 * SwitchgearNode – Switchgear visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with phase lights, moving needles & breaker status flags
 * - Premium glassmorphism layout
 * - Classic industrial SVG layout
 */
const SwitchgearNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticSwitchgear
              liveValue={liveValue}
              unit={unit || "A"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const isClosed = isRunning; // closed = conducting
        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

        const pulseDuration = isRunning ? `${Math.max(0.3, 1.0 / (speedScale || 1))}s` : "0s";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="70"
            height="78"
            viewBox="0 0 70 78"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  {/* Metallic cabinet gradient */}
                  <linearGradient id="swgCabinet" x1="0" y1="0" x2="70" y2="70">
                    <stop offset="0%" stopColor="#334155" />
                    <stop offset="50%" stopColor="#475569" />
                    <stop offset="100%" stopColor="#1e293b" />
                  </linearGradient>
                  <filter id="electricArc" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="1.5" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>

                {/* Cabinet housing */}
                <rect x="12" y="4" width="50" height="58" rx="3" fill="url(#swgCabinet)" stroke="#64748b" strokeWidth="1.5" />

                {/* Horizontal partition line */}
                <line x1="12" y1="28" x2="62" y2="28" stroke="#1e293b" strokeWidth="1.5" />

                {/* Upper Section: Monitoring & Controls */}
                {/* Mimic screen grid */}
                <rect x="18" y="9" width="38" height="14" rx="1" fill="#0f172a" stroke={isClosed ? "#22c55e" : "#ef4444"} strokeWidth="1" />
                {/* Waveform or status grid inside screen */}
                <path d="M 20,16 L 25,16 L 27,11 L 30,21 L 32,16 L 40,16 L 42,12 L 44,18 L 46,16 L 54,16"
                      stroke={isClosed ? "#22c55e" : "#ef4444"} strokeWidth="1.5" fill="none" opacity="0.9" filter={isClosed ? "url(#electricArc)" : "none"}>
                  {isClosed ? (
                    <animate attributeName="stroke-dasharray" values="50;10;50" dur="1s" repeatCount="indefinite" />
                  ) : (
                    <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />
                  )}
                </path>

                {/* Lower Section: Breaker Drawout compartment */}
                {/* Mimic Busbar line running horizontally across lower section */}
                {/* Busbar In */}
                <line x1="4" y1="45" x2="26" y2="45" stroke={isClosed ? "#22c55e" : "#475569"} strokeWidth="3" strokeLinecap="round" />
                {/* Busbar Out */}
                <line x1="48" y1="45" x2="70" y2="45" stroke={isClosed ? "#22c55e" : "#475569"} strokeWidth="3" strokeLinecap="round" />

                {/* Contact terminals */}
                <circle cx="26" cy="45" r="3.5" fill="#f8fafc" stroke="#1e293b" strokeWidth="1" />
                <circle cx="48" cy="45" r="3.5" fill="#f8fafc" stroke="#1e293b" strokeWidth="1" />

                {/* Switch contact arm */}
                <line
                  x1="26" y1="45"
                  x2={isClosed ? 48 : 42}
                  y2={isClosed ? 45 : 31}
                  stroke={isClosed ? "#22c55e" : "#ef4444"}
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  style={{ transition: "all 0.3s ease" }}
                />

                {/* High-visibility arc/discharge spark when open and online */}
                {!isClosed && !isOffline && (
                  <g filter="url(#electricArc)">
                    {/* Animated electrical spark between contact arm and output terminal */}
                    <path
                      d="M 42,31 L 44,38 L 43,41 L 48,45"
                      stroke="#fbbf24"
                      strokeWidth="2.5"
                      fill="none"
                      strokeLinecap="round"
                    >
                      <animate
                        attributeName="d"
                        values="
                          M 42,31 L 44,38 L 43,41 L 48,45;
                          M 42,31 L 46,36 L 41,40 L 48,45;
                          M 42,31 L 41,35 L 47,41 L 48,45;
                          M 42,31 L 44,38 L 43,41 L 48,45
                        "
                        dur="0.25s"
                        repeatCount="indefinite"
                      />
                      <animate attributeName="opacity" values="0.3;1;0.5;1;0.3" dur="0.1s" repeatCount="indefinite" />
                      <animate attributeName="stroke" values="#fbbf24;#38bdf8;#f43f5e;#fbbf24" dur="0.3s" repeatCount="indefinite" />
                    </path>
                  </g>
                )}

                {/* Electrical flow animation when closed */}
                {isClosed && (
                  <g filter="url(#electricArc)">
                    {/* Flowing dashes on the main busbars */}
                    <line x1="4" y1="45" x2="70" y2="45" stroke="#fef08a" strokeWidth="3.5" strokeDasharray="8 10">
                      <animate attributeName="stroke-dashoffset" values="0;36" dur="0.6s" repeatCount="indefinite" />
                    </line>
                  </g>
                )}

                {/* Premium status dot */}
                <circle cx="56" cy="11" r="5" fill={dotColor} stroke="#ffffff" strokeWidth="1.5" filter={isRunning ? "url(#electricArc)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>

                {/* Status Text label */}
                <text x="35" y="73" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
                  {statusColor === "green" ? "CLOSED" : statusColor === "red" ? "OPEN" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Housing */}
                <rect
                  x="10" y="5" width="50" height="40" rx="4"
                  fill={isClosed ? `${color}12` : `${color}05`}
                  stroke={isClosed ? color : "#64748b"}
                  strokeWidth="2"
                />

                {/* Bus bar in */}
                <line x1="0" y1="25" x2="25" y2="25" stroke={dotColor} strokeWidth="3" />

                {/* Breaker contact */}
                <line
                  x1="25" y1="25"
                  x2={isClosed ? "45" : "40"}
                  y2={isClosed ? "25" : "12"}
                  stroke={dotColor}
                  strokeWidth="3"
                  strokeLinecap="round"
                  style={{ transition: "all 0.3s ease" }}
                />

                {/* Bus bar out */}
                <line x1="45" y1="25" x2="70" y2="25" stroke={dotColor} strokeWidth="3" />

                {/* Contact points */}
                <circle cx="25" cy="25" r="3" fill={color} />
                <circle cx="45" cy="25" r="3" fill={color} />

                {/* Arc when open */}
                {!isClosed && !isOffline && (
                  <path
                    d="M28 22 Q35 15 42 22"
                    stroke="#fbbf24"
                    strokeWidth="1.5"
                    fill="none"
                    strokeDasharray="2 2"
                    opacity="0.6"
                  />
                )}

                {/* Current flow when closed */}
                {isClosed && (
                  <g style={{ animation: `live-electrical-pulse ${pulseDuration} ease-in-out infinite` }}>
                    <line x1="25" y1="25" x2="45" y2="25" stroke={dotColor} strokeWidth="1.5" opacity="0.5" />
                  </g>
                )}

                {/* Status dot */}
                <circle cx="55" cy="10" r="4" fill={dotColor} />
                <text x="35" y="73" textAnchor="middle" fontSize="8" fontWeight="600" fill={dotColor}>
                  {statusColor === "green" ? "CLOSED" : statusColor === "red" ? "OPEN" : "OFFLINE"}
                </text>
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

SwitchgearNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default SwitchgearNode;
